/**
 * Tile component module.
 * @module shared/components/Tile
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {fill, layoutAttribute} from './cssUtils';
import Row from './Row';
import {_Overlay} from './GridTile';

export const _Title = styled.span`
  text-align: center;
  //width: 100%;
  margin: 5px;
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.95)'};
  font-family: ${props => props.theme.titleText.name};
  font-size: ${props => props.theme.titleText.size};
  line-height: ${props => props.theme.titleText.lineHeight};
  ${props => props.theme.neumorphic ? 'text-transform: uppercase' : ''};
`;

export const _Divider = styled.hr`
  border-style: solid;
  color: ${(props) => props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  ${props => props.theme.neumorphic ? `color: ${props.theme.primaryColor}` : ''};
  ${props => props.theme.neumorphic ? 'opacity: 1' : ''};
  border-width: 1px;
  ${fill()}
  margin: 5px;
`;

const _Tile = styled.div`
  position: relative;
  box-sizing: border-box;
  background-color: ${props => props.theme.tileBackgroundColor};
  ${(props) => props.theme.dark ? 'background-image: linear-gradient(to bottom right, rgba(0, 29, 40, 0.3), rgba(9, 16, 0, 0.3))' : 'background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.18), rgba(166, 198, 205, 0.18), rgba(255, 255, 255, 0.18))'};
  ${(props) => props.theme.dark ? '' : 'filter: drop-shadow(2px 2px 3px rgba(0,0,0,0.3))'};
  ${props => props.theme.neumorphic ? 'background-image: none' : ''};
  ${props => props.theme.neumorphic
    ? `box-shadow: 2px 2px 5px 2px rgba(0, 0, 0, 0.4),
                   1px 1px 0 0 rgba(0, 0, 0, 0.5),
                   -1px -1px 0 0 rgba(255, 255, 255, 0.2)` : ''};
  border-radius: 12px;
  padding: 5px;
  margin: ${props => props.margin};
  height: ${props => props.height || 'fit-content'};
  ${props => !!props.minHeight ? `min-height: ${props.minHeight};` : ''};
  display: flex;
  flex-direction: column;
  ${props => fill(props.width)};
  ${props => !!props.onClick && `
    cursor: pointer;
  `};
  ${layoutAttribute.css}
  ${props => props.maxWidth && `max-width: ${props.maxWidth}`};
  ${props => props.minWidth && `min-width: ${props.minWidth}`};
`;

/**
 * Tile component
 * @name Tile
 */
const Tile = (props) => {
  const {
    title,
    height,
    width,
    action,
    margin = '10px',
    editing,
    minWidth,
    minHeight,
    maxWidth,
    layout,
    icon,
    actionIcon,
  } = props;
  const attributes = {
    height,
    margin,
    minWidth,
    maxWidth,
    minHeight,
    width,
    layout,
    onClick: action,
  };

  return <_Tile {...attributes}>
    {title && <Row justifySubRow={'space between'} wrap={false}>
      {!!icon ? icon : <div style={{width: '30px'}}>&nbsp;</div>}
      <_Title>{title}</_Title>
      {!!actionIcon ? actionIcon : <div style={{width: '30px'}}>&nbsp;</div>}
    </Row>}
    {title && <_Divider/>}
    {props.children}
    {editing && <_Overlay/>}
  </_Tile>;
};

export default Tile;

Tile.propTypes = {
  /**
   * Title of Tile
   */
  title: PropTypes.string,
  /**
   * React Element children
   */
  children: PropTypes.node,
  /**
   * The content to display inside Tile
   */
  content: PropTypes.shape({
    body: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.element,
    ]).isRequired,
  }),
  /**
   * Height of the content to display inside Tile
   */
  height: PropTypes.string,
  /**
   * Minimum Height of the content to display inside Tile
   */
  minHeight: PropTypes.string,
  /**
   * Minimum Width of the content to display inside Tile
   */
  minWidth: PropTypes.string,
  /**
   * Max Width of the content to display inside Tile
   */
  maxWidth: PropTypes.string,
  /**
   * Width of the content to display inside Tile
   */
  width: PropTypes.string,
  layout: layoutAttribute.propTypes,
  /**
   * Optional on click action for entire tile.
   */
  action: PropTypes.func,
  /**
   * Optional margin defaults to 5px
   */
  margin: PropTypes.string,
  /**
   * Indicates if layout is being edited and should dim the content.
   */
  editing: PropTypes.bool,
  /**
   * Icon to display on left side
   */
  icon: PropTypes.node,
  /**
   * Right action icon to display on right side
   */
  actionIcon: PropTypes.node,
};
